import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  TrendingUp,
  Info,
} from "lucide-react";

const TopKFrequentElementsVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Basic Example",
      nums: [1, 1, 1, 2, 2, 3],
      k: 2,
    },
    {
      id: 2,
      name: "Single Element",
      nums: [1],
      k: 1,
    },
    {
      id: 3,
      name: "Complex Case",
      nums: [1, 2, 1, 2, 1, 2, 3, 1, 3, 2],
      k: 2,
    },
    {
      id: 4,
      name: "Multiple Frequencies",
      nums: [4, 1, -1, 2, -1, 2, 3],
      k: 2,
    },
    {
      id: 5,
      name: "Full Array",
      nums: [1, 2, 3, 4, 5],
      k: 5,
    },
    {
      id: 6,
      name: "Tied Frequencies",
      nums: [6, 6, 7, 7, 8, 8, 1, 2, 3],
      k: 3,
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customNums, setCustomNums] = useState("");
  const [customK, setCustomK] = useState("");
  const [nums, setNums] = useState(defaultTestCases[0].nums);
  const [k, setK] = useState(defaultTestCases[0].k);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function topKFrequent(nums, k):", indent: 0 },
    { line: "// Step 1: Count frequencies", indent: 1 },
    { line: "freqMap = {}", indent: 1 },
    { line: "for num in nums:", indent: 1 },
    { line: "freqMap[num] = (freqMap[num] || 0) + 1", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Step 2: Create frequency buckets", indent: 1 },
    { line: "buckets = new Array(nums.length + 1)", indent: 1 },
    { line: "for num, freq in freqMap:", indent: 1 },
    { line: "if (!buckets[freq]) buckets[freq] = []", indent: 2 },
    { line: "buckets[freq].push(num)", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Step 3: Extract top k elements", indent: 1 },
    { line: "result = []", indent: 1 },
    { line: "for i = buckets.length-1 down to 0:", indent: 1 },
    { line: "if buckets[i]:", indent: 2 },
    { line: "result.push(...buckets[i])", indent: 3 },
    { line: "if result.length == k: return result", indent: 3 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (nums, k) => {
    const allSteps = [];
    let stepCount = 0;

    const addStep = (description, highlightLine, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description,
        highlightLine,
        ...details,
      });
    };

    // Step 1: Count frequencies
    addStep("Start: Input array and k.", 0, {
      phase: "init",
      freqMap: {},
      buckets: [],
      result: [],
    });
    let freqMap = {};
    addStep("Step 1: Count frequency of each number.", 1, {
      phase: "freq",
      freqMap: {},
      buckets: [],
      result: [],
    });
    for (let i = 0; i < nums.length; i++) {
      const num = nums[i];
      addStep(`Processing nums[${i}]: ${num}.`, 3, {
        phase: "freq",
        freqMap: { ...freqMap },
        currentIndex: i,
        buckets: [],
        result: [],
      });
      freqMap[num] = (freqMap[num] || 0) + 1;
      addStep(`Count of ${num} is now ${freqMap[num]}.`, 4, {
        phase: "freq",
        freqMap: { ...freqMap },
        currentIndex: i,
        buckets: [],
        result: [],
      });
    }

    // Step 2: Bucketing
    addStep("Step 2: Group numbers into buckets by frequency.", 6, {
      phase: "bucket",
      freqMap,
      buckets: new Array(nums.length + 1).fill(null),
      result: [],
    });
    let buckets = new Array(nums.length + 1).fill(null).map(() => []);
    for (const num in freqMap) {
      const freq = freqMap[num];
      addStep(
        `Number ${num} has frequency ${freq}. Placing in bucket ${freq}.`,
        9,
        {
          phase: "bucket",
          freqMap,
          buckets: [...buckets.map((b) => [...b])],
          currentNum: num,
          result: [],
        }
      );
      buckets[freq].push(parseInt(num));
      addStep(`Placed ${num} in bucket ${freq}.`, 10, {
        phase: "bucket",
        freqMap,
        buckets: [...buckets.map((b) => [...b])],
        currentNum: num,
        result: [],
      });
    }

    // Step 3: Extract from buckets
    addStep("Step 3: Extract elements from buckets in reverse order.", 13, {
      phase: "extract",
      freqMap,
      buckets,
      result: [],
    });
    let result = [];
    for (let i = buckets.length - 1; i >= 0; i--) {
      if (result.length >= k) break;
      addStep(`Checking bucket ${i}.`, 15, {
        phase: "extract",
        freqMap,
        buckets,
        result: [...result],
        currentBucketIndex: i,
      });
      if (buckets[i] && buckets[i].length > 0) {
        addStep(`Bucket ${i} has elements: [${buckets[i].join(", ")}].`, 16, {
          phase: "extract",
          freqMap,
          buckets,
          result: [...result],
          currentBucketIndex: i,
        });
        result.push(...buckets[i]);
        addStep(`Added [${buckets[i].join(", ")}] to result.`, 17, {
          phase: "extract",
          freqMap,
          buckets,
          result: [...result],
          currentBucketIndex: i,
        });
      }
    }

    const finalResult = result.slice(0, k);
    addStep(`Final result: Top ${k} frequent elements.`, 18, {
      phase: "done",
      freqMap,
      buckets,
      result: finalResult,
      isFinal: true,
    });

    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(nums, k);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [nums, k]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setNums(testCases[index].nums);
    setK(testCases[index].k);
  };

  const addCustomTestCase = () => {
    try {
      const parsedNums = JSON.parse(`[${customNums}]`);
      const parsedK = parseInt(customK);
      if (
        !Array.isArray(parsedNums) ||
        !parsedNums.every((n) => typeof n === "number")
      ) {
        throw new Error("Invalid nums format.");
      }
      if (isNaN(parsedK) || parsedK <= 0) {
        throw new Error("k must be a positive number.");
      }
      const newTestCase = {
        id: Date.now(),
        name: `Custom`,
        nums: parsedNums,
        k: parsedK,
      };
      setTestCases([...testCases, newTestCase]);
      setCustomNums("");
      setCustomK("");
    } catch (error) {
      alert(
        `Invalid format. ${error.message} Use comma-separated numbers for the array.`
      );
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <TrendingUp className="text-blue-500" size={24} /> Top K Frequent
        Elements
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given an integer array `nums` and an integer `k`, return the `k`
                most frequent elements. You may return the answer in any order.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      nums: [{tc.nums.join(", ")}], k: {tc.k}
                    </div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customNums}
                onChange={(e) => setCustomNums(e.target.value)}
                placeholder="Nums: 1,1,2,3"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <input
                type="number"
                value={customK}
                onChange={(e) => setCustomK(e.target.value)}
                placeholder="k: 2"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            {/* Frequencies */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2 text-gray-700">
                Frequencies
              </h4>
              <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded border">
                {currentStepData &&
                Object.keys(currentStepData.freqMap).length > 0 ? (
                  Object.entries(currentStepData.freqMap).map(([num, freq]) => (
                    <div
                      key={num}
                      className={`p-2 rounded text-center transition-all duration-300 ${
                        currentStepData.currentNum === num
                          ? "bg-blue-200"
                          : "bg-gray-200"
                      }`}
                    >
                      <div className="font-mono font-bold text-lg">{num}</div>
                      <div className="text-xs text-gray-600">Count: {freq}</div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-500">Counting...</p>
                )}
              </div>
            </div>

            {/* Buckets */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2 text-gray-700">
                Buckets (by Frequency)
              </h4>
              <div className="flex flex-wrap items-start gap-2 p-2 bg-gray-50 rounded border min-h-[50px]">
                {currentStepData?.buckets?.map(
                  (bucket, index) =>
                    bucket &&
                    bucket.length > 0 && (
                      <div
                        key={index}
                        className={`p-2 rounded text-center border-2 transition-all duration-300 ${
                          currentStepData.currentBucketIndex === index
                            ? "bg-yellow-200 border-yellow-400"
                            : "bg-gray-100 border-gray-300"
                        }`}
                      >
                        <div className="text-xs text-gray-600 mb-1">
                          Freq {index}
                        </div>
                        <div className="flex gap-1">
                          {bucket.map((num) => (
                            <div
                              key={num}
                              className="w-8 h-8 flex items-center justify-center bg-white rounded font-mono font-bold"
                            >
                              {num}
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                )}
              </div>
            </div>

            {/* Result */}
            <div>
              <h4 className="text-sm font-semibold mb-2 text-gray-700">
                Result (Top K)
              </h4>
              <div className="flex flex-wrap gap-2 p-2 bg-green-50 rounded border min-h-[50px]">
                {currentStepData?.result?.map((num) => (
                  <div
                    key={num}
                    className="w-10 h-10 flex items-center justify-center bg-green-200 rounded font-mono font-bold text-lg"
                  >
                    {num}
                  </div>
                ))}
              </div>
            </div>
            {currentStepData?.isFinal && (
              <div className="mt-3 p-3 bg-green-100 rounded">
                <h4 className="text-sm font-bold text-green-900">Success!</h4>
                <p className="text-xs mt-1 text-green-700">
                  The top {k} frequent elements are: [
                  {currentStepData.result.join(", ")}]
                </p>
              </div>
            )}
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="100"
                  max="1500"
                  step="100"
                  value={1600 - speed}
                  onChange={(e) => setSpeed(1600 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Bucket Sort Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              A highly efficient approach is using Bucket Sort. First, count the
              frequency of each number using a hash map. Then, create an array
              of "buckets," where the index represents the frequency. Place
              numbers into the bucket corresponding to their frequency. Finally,
              iterate backward from the last bucket to collect the most frequent
              elements.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(N)
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              N = number of elements in the input array.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  This method is faster than a heap-based solution (which is O(N
                  log k)) because it avoids comparisons.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The maximum possible frequency is the length of the input
                  array, so the number of buckets is known in advance.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Iterating from the end of the buckets guarantees we get the
                  most frequent elements first.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded bg-blue-200 font-bold">1</div>
                <span>Current number being processed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded border-2 border-yellow-400 bg-yellow-200">
                  ...
                </div>
                <span>Current bucket being checked</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-green-200 rounded font-bold">
                  1
                </div>
                <span>Element in result</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopKFrequentElementsVisualizer;
